import { createSlice } from '@reduxjs/toolkit';
import {
  addReportRecipientAPI,
  deleteReportRecipientAPI,
  downloadBudgetSummaryReport,
  getOrDownloadCheckInvoicePDFsReport,
  getOrDownloadFacilitiesInvoiceReport,
  getOrDownloadFacilityComparisonReport,
  getOrDownloadFacilityHistoryReport,
  getOrDownloadInvoiceIssuesReport,
  getOrDownloadItemsOrderedReport,
  getOrDownloadRentalSerialNumberReport,
  getOrDownloadPoInvoiceMatchupReports,
  getOrDownloadSubstituteReport,
  getOrDownloadTopLevelBudgetReport,
  getOrDownloadVendorComparisonReport,
  downloadOperationsReport,
  getReportOptions,
  deleteReportScheduleAPI,
  getReportSchedules,
  getReports,
  updateActiveAPI,
  getRecipientEmails,
  downloadInvoicePDFs,
  getOrDownloadUsersReport,
  getOrDownloadCensusIntegrationReport,
  getOrDownloadCensusReport,
  downloadInvoiceBookedReport,
  getOrDownloadPriceChangeReport,
  getOrDownloadRejectedItemsByVendorReport,
  getOrDownloadBidSheetReport,
  getOrDownloadBudgetsReport,
  getOrDownloadTransportRequestReport,
  getOrDownloadTransportTripsByDoctorTypeReport,
} from '../../api/reports';
import { checkForToMuchData } from '../../components/dashboard/reports/ReportViewHelper';
import {
  TRANSPORT_REQUEST_REPORT_ID,
  TRANSPORT_TRIPS_BY_DOCTOR_TYPE_REPORT_ID,
} from '../../components/dashboard/reports/Constants';

const initialState = {
  isLoading: false,
  error: null,
  data: {
    loadingMainData: false,
    loadingReportSpecificationOptions: false,
    loadingReportResults: false,
    loadingReportScheduleResults: false,
    downloadingReportResults: false,
    activeStep: 0,
    reportColumns: {},
    openSidebar: true,
    showDatePicker: false,
    formValues: {
      facilities: null,
      vendors: null,
      categories: null,
      subcategories: null,
      skip: 0,
      take: 100,
      serialNumbers: [],
      rentalVendor: null,
      transportStatuses: null,
    },
    reportSchedules: [],
    selectedReportId: 0,
    createReportData: {},
    reportResults: [],
    reportSpecificationOptions: [],
    dynamicColumns: null,
    toMuchData: false,
  },
};

const slice = createSlice({
  name: 'reports',
  initialState,
  reducers: {
    startLoading(state) {
      state.isLoading = true;
    },
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },
    getReportsSuccess: (state, action) => {
      state.isLoading = false;
      state.data = action.payload;
    },
    setReportData: (state, action) => {
      state.data.createReportData = action.payload;
    },

    setFormValues: (state, action) => {
      state.data.formValues = action.payload;
    },
    setReportResults: (state, action) => {
      state.data.reportResults = action.payload;
    },
    setSelectedReportId: (state, action) => {
      state.data.selectedReportId = action.payload;
    },
    setReportSpecificationOptions: (state, action) => {
      state.data.reportSpecificationOptions = action.payload;
    },
    setLoadingMainData: (state, action) => {
      state.data.loadingMainData = action.payload;
    },
    setLoadingReportSpecificationOptions: (state, action) => {
      state.data.loadingReportSpecificationOptions = action.payload;
    },
    setLoadingReportResults: (state, action) => {
      state.data.loadingReportResults = action.payload;
    },
    setLoadingReportScheduleResults: (state, action) => {
      state.data.loadingReportScheduleResults = action.payload;
    },
    setActiveStep: (state, action) => {
      state.data.activeStep = action.payload;
    },
    setDownloadingReportResults: (state, action) => {
      state.data.downloadingReportResults = action.payload;
    },

    fetchReportsStart: (state) => {
      state.data.loadingMainData = true;
    },
    fetchReportsSuccess: (state, action) => {
      state.data.loadingMainData = false;
      state.data.createReportData = action.payload;
      state.data.createReportData.companies = state.data.createReportData.companies.sort((a, b) =>
        a.label.localeCompare(b.label)
      );
      state.data.createReportData.reports = state.data.createReportData.reports
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((report) => ({ ...report, value: report.id, label: report.name }));
      state.data.createReportData.reportTypes = state.data.createReportData.reportTypes
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((type) => ({ ...type, value: type.id, label: type.name }));
      state.data.createReportData.facilities = state.data.createReportData.facilities.map((facility) => ({
        ...facility,
        value: facility.id,
        label: facility.name,
      }));
      state.data.createReportData.subcategories = state.data.createReportData.subcategories.map((subcategory) => ({
        ...subcategory,
        value: subcategory.id,
        label: subcategory.name,
      }));
      state.data.createReportData.categories = state.data.createReportData.categories.map((category) => ({
        ...category,
        value: category.id,
        label: category.name,
      }));
      state.data.createReportData.timeFrameFilters = state.data.createReportData.timeFrameFilters.map((category) => ({
        ...category,
        value: category.id,
        label: category.name,
      }));
      state.data.createReportData.ftpSites = state.data.createReportData.ftpSites.map((f) => ({
        ...f,
        value: f.id,
        label: f.host,
      }));
    },
    fetchReportsFailure: (state, action) => {
      state.data.loadingMainData = false;
      state.error = action.payload;
    },
    fetchReportOptionsStart: (state) => {
      state.data.loadingReportSpecificationOptions = true;
    },
    fetchReportOptionsSuccess: (state, action) => {
      state.data.reportSpecificationOptions = action.payload;
      state.data.formValues.useBudgetDate ??= false;
      const presetDate = state.data.reportSpecificationOptions.some((o) => o.name === 'DateFilter' && !o.multiselect);
      state.data.formValues.startDate = presetDate ? new Date(new Date().setDate(new Date().getDate() - 29)) : null;
      state.data.formValues.endDate = presetDate ? new Date() : null;
      state.data.formValues.startBetweenDate = presetDate
        ? new Date(new Date().setDate(new Date().getDate() - 29))
        : null;
      state.data.formValues.endBetweenDate = presetDate ? new Date() : null;
      state.data.loadingReportSpecificationOptions = false;
      state.data.activeStep = 1;
    },
    getReportSchedulesSuccess: (state, action) => {
      state.data.reportSchedules = action.payload;
    },
    getReportRecipientSuccess: (state, action) => {
      state.data.reportRecipients = action.payload;
    },
    addReportRecipientSuccess: (state, action) => {
      const recipients = action.payload;
      const index = state.data.reportSchedules.findIndex((i) => i.id === recipients[0]?.extraValue);
      state.data.reportSchedules[index].recipients = action.payload;
    },
    deleteReportRecipientSuccess: (state, action) => {
      const { scheduleId, recipientId } = action.payload;
      const index = state.data.reportSchedules.findIndex((i) => i.id === scheduleId);
      state.data.reportSchedules[index].recipients = state.data.reportSchedules[index].recipients.filter(
        (s) => s.value !== recipientId
      );
    },
    deleteReportScheduleSuccess: (state, action) => {
      const { scheduleId } = action.payload;

      state.data.reportSchedules = state.data.reportSchedules.filter((s) => s.id !== scheduleId);
    },
    updateActiveReportSuccess: (state, action) => {
      const { scheduleId, active } = action.payload;
      const index = state.data.reportSchedules.findIndex((i) => i.id === scheduleId);
      state.data.reportSchedules[index].active = active;
    },
    fetchReportOptionsFailure: (state, action) => {
      state.data.loadingReportSpecificationOptions = false;
      state.error = action.payload;
    },
    reportTimeout: (state) => {
      state.data.loadingReportResults = false;
      state.data.reportResults = [];
    },
    setReportResultsStart: (state) => {
      state.data.loadingReportResults = true;
    },
    setReportResultsSuccess: (state, action) => {
      const { toMuchData, reportColumns, dynamicColumns, reportResults } = action.payload;
      state.data.reportResults = reportResults;
      state.data.activeStep = 2;
      state.data.reportColumns = reportColumns;
      state.data.loadingReportResults = false;
      state.data.toMuchData = toMuchData;
      state.data.dynamicColumns = dynamicColumns;
    },
    setReportResultsFailure: (state, action) => {
      state.data.loadingReportResults = false;
      state.error = action.payload;
    },
    downloadReportResultsStart: (state) => {
      state.data.downloadingReportResults = true;
    },
    downloadReportResultsSuccess: (state, action) => {
      state.data.downloadingReportResults = false;
      state.error = action.payload;
    },
    downloadReportResultsFailure: (state, action) => {
      state.data.downloadingReportResults = false;
      state.error = action.payload;
    },
    setToMuchData: (state, action) => {
      state.data.toMuchData = action.payload;
    },
  },
});

export function updateActiveStep(step) {
  return async (dispatch) => {
    dispatch(slice.actions.setActiveStep(step));
  };
}

export function updateFormValues(value, callback = null) {
  return async (dispatch) => {
    dispatch(slice.actions.setFormValues(value));
    if (callback) callback(value);
  };
}

export function clearFormValues() {
  const value = {
    facilities: null,
    vendors: null,
    categories: null,
    subcategories: null,
    skip: 0,
    take: 100,
    ftpSites: null,
    serialNumbers: [],
    transportStatuses: null,
  };
  return async (dispatch) => {
    dispatch(slice.actions.setFormValues(value));
  };
}

export function updateSelectedReportId(value) {
  return async (dispatch) => {
    dispatch(slice.actions.setSelectedReportId(value));
  };
}

export function updateToMuchData(value) {
  return async (dispatch) => {
    dispatch(slice.actions.setToMuchData(value));
  };
}

export function updateReportResults(value) {
  return async (dispatch) => {
    dispatch(slice.actions.setReportResults(value));
  };
}

export function reportTimeout() {
  return async (dispatch) => {
    dispatch(slice.actions.reportTimeout());
  };
}

export function fetchReports() {
  return async (dispatch) => {
    dispatch(slice.actions.fetchReportsStart());
    try {
      const response = await getReports();
      dispatch(slice.actions.fetchReportsSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.fetchReportsFailure(error.message));
    }
  };
}

export const fetchReportOptions = (selectedReportId) => async (dispatch) => {
  dispatch(slice.actions.fetchReportOptionsStart());
  try {
    const response = await getReportOptions(selectedReportId);
    dispatch(slice.actions.fetchReportOptionsSuccess(response.data));
  } catch (error) {
    dispatch(slice.actions.fetchReportOptionsFailure(error.message));
  }
};

export function fetchReportSchedules() {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.setLoadingReportScheduleResults(true));
      const response = await getReportSchedules();
      const responseRecip = await getRecipientEmails();
      dispatch(slice.actions.getReportSchedulesSuccess(response.data));
      dispatch(slice.actions.getReportRecipientSuccess(responseRecip.data));
      dispatch(slice.actions.setLoadingReportScheduleResults(false));
    } catch (error) {
      dispatch(slice.actions.fetchReportsFailure(error.message));
      dispatch(slice.actions.setLoadingReportScheduleResults(false));
    }
  };
}

export function addReportRecipient(recipientEmail, scheduleId) {
  return async (dispatch) => {
    try {
      const response = await addReportRecipientAPI(recipientEmail, scheduleId);
      dispatch(slice.actions.addReportRecipientSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.fetchReportsFailure(error.message));
    }
  };
}

export function deleteReportSchedule(scheduleId) {
  return async (dispatch) => {
    try {
      const response = await deleteReportScheduleAPI(scheduleId);
      if (response.data.success) {
        dispatch(slice.actions.deleteReportScheduleSuccess({ scheduleId }));
      }
    } catch (error) {
      dispatch(slice.actions.fetchReportsFailure(error.message));
    }
  };
}

export function deleteReportRecipient(recipientId, scheduleId) {
  return async (dispatch) => {
    try {
      const response = await deleteReportRecipientAPI(recipientId);
      if (response.data.success) {
        dispatch(slice.actions.deleteReportRecipientSuccess({ recipientId, scheduleId }));
      }
    } catch (error) {
      dispatch(slice.actions.fetchReportsFailure(error.message));
    }
  };
}

export function updateActiveReport(scheduleId, active) {
  return async (dispatch) => {
    try {
      const response = await updateActiveAPI(scheduleId, active);
      if (response.data.success) {
        dispatch(slice.actions.updateActiveReportSuccess({ scheduleId, active }));
      }
    } catch (error) {
      dispatch(slice.actions.fetchReportsFailure(error.message));
    }
  };
}

export function downloadReportResults(selectedReportId, formValues, download, columns, sendAsEmail) {
  return async (dispatch) => {
    let response;
    if (download) dispatch(slice.actions.downloadReportResultsStart());
    else dispatch(slice.actions.setReportResultsStart());
    try {
      if (selectedReportId === 1)
        response = await getOrDownloadItemsOrderedReport(formValues, false, download, sendAsEmail);
      else if (selectedReportId === 2)
        response = await getOrDownloadFacilityComparisonReport(formValues, download, sendAsEmail);
      else if (selectedReportId === 3)
        response = await getOrDownloadVendorComparisonReport(formValues, download, sendAsEmail);
      else if (selectedReportId === 4)
        response = await getOrDownloadTopLevelBudgetReport(formValues, download, sendAsEmail);
      else if (selectedReportId === 5)
        response = await getOrDownloadFacilityHistoryReport(formValues, download, sendAsEmail);
      else if (selectedReportId === 6)
        response = await getOrDownloadPoInvoiceMatchupReports(formValues, download, false, sendAsEmail);
      else if (selectedReportId === 7)
        response = await getOrDownloadSubstituteReport(formValues, download, sendAsEmail);
      else if (selectedReportId === 8)
        response = await getOrDownloadFacilitiesInvoiceReport(formValues, download, sendAsEmail);
      else if (selectedReportId === 9)
        response = await getOrDownloadCheckInvoicePDFsReport(formValues, download, sendAsEmail);
      else if (selectedReportId === 10)
        response = await getOrDownloadInvoiceIssuesReport(formValues, download, sendAsEmail);
      else if (selectedReportId === 11)
        response = await getOrDownloadItemsOrderedReport(formValues, true, download, sendAsEmail);
      else if (selectedReportId === 12) response = await downloadBudgetSummaryReport(formValues);
      else if (selectedReportId === 13) response = await getOrDownloadUsersReport(formValues, download, sendAsEmail);
      else if (selectedReportId === 14)
        response = await getOrDownloadPoInvoiceMatchupReports(formValues, download, true, sendAsEmail);
      else if (selectedReportId === 15) response = await downloadOperationsReport(formValues);
      else if (selectedReportId === 16) response = await downloadInvoicePDFs(formValues);
      else if (selectedReportId === 18)
        response = await getOrDownloadCensusIntegrationReport(formValues, download, sendAsEmail);
      else if (selectedReportId === 20) response = await downloadInvoiceBookedReport(formValues);
      else if (selectedReportId === 23)
        response = await getOrDownloadRentalSerialNumberReport(formValues, download, sendAsEmail, true);
      else if (selectedReportId === 21)
        response = await getOrDownloadPriceChangeReport(formValues, download, sendAsEmail);
      else if (selectedReportId === 26) response = await getOrDownloadBidSheetReport(formValues, download, sendAsEmail);
      else if (selectedReportId === 24) response = await getOrDownloadCensusReport(formValues, download, sendAsEmail);
      else if (selectedReportId === TRANSPORT_REQUEST_REPORT_ID)
        response = await getOrDownloadTransportRequestReport(formValues, download, sendAsEmail);
      else if (selectedReportId === 27)
        response = await getOrDownloadRejectedItemsByVendorReport(formValues, download, sendAsEmail);
      else if (selectedReportId === 28) response = await getOrDownloadBudgetsReport(formValues, download, sendAsEmail);
      else if (selectedReportId === TRANSPORT_TRIPS_BY_DOCTOR_TYPE_REPORT_ID)
        response = await getOrDownloadTransportTripsByDoctorTypeReport(formValues, download, sendAsEmail);
      if (response === 'Timeout') return response;
      if (!download) dispatch(slice.actions.setReportResultsSuccess(checkForToMuchData(columns, response?.data ?? [])));
      else dispatch(slice.actions.downloadReportResultsSuccess(response?.data));
    } catch (error) {
      if (!download) dispatch(slice.actions.setReportResultsFailure(error.message));
      else dispatch(slice.actions.downloadReportResultsFailure(error.message));
    }
    return response;
  };
}
export default slice.reducer;
